import { httpClient } from '@/shared/api/httpClient'

/**
 * Incidencias que solo ve el cliente y nos cuenta (`POST /client-events`, tabla `client_events`):
 * una pantalla que no termina de cargar (spec 2026-11-02-atasco-navegacion-app) y los errores
 * de la app nativa (spec 2026-11-02-errores-app-nativa). Se guardan
 * primero en una cola local y se mandan después: la incidencia puede acabar recargando la app, y
 * así se envía al volver a arrancar en vez de perderse.
 */

export type ClientEventType = 'CHUNK_LOAD_STALLED' | 'CLIENT_ERROR'

export interface ClientEvent {
  type: ClientEventType
  path?: string
  detail?: string
  /** Principio de la traza (solo `CLIENT_ERROR`). */
  stack?: string
  /** ISO; cuándo pasó en el dispositivo (puede mandarse más tarde). */
  occurredAt: string
}

const STORAGE_KEY = 'mp:client-events'
/** Techo de la cola: si el servidor no responde durante mucho tiempo no crece sin fin. */
export const MAX_QUEUED_EVENTS = 20

type Store = Pick<Storage, 'getItem' | 'setItem'>

function storage(): Store | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readQueue(store: Store): ClientEvent[] {
  try {
    const parsed: unknown = JSON.parse(store.getItem(STORAGE_KEY) ?? '[]')
    return Array.isArray(parsed) ? (parsed as ClientEvent[]) : []
  } catch {
    return []
  }
}

function writeQueue(store: Store, events: ClientEvent[]): void {
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_QUEUED_EVENTS)))
  } catch {
    /* almacenamiento lleno o bloqueado: se pierde la medición, no la app */
  }
}

let flushing: Promise<void> | null = null

/**
 * Manda la cola en orden y quita lo enviado. Para en el primer fallo y lo deja para el próximo
 * intento (al arrancar o en la siguiente incidencia).
 */
export function flushClientEvents(): Promise<void> {
  flushing ??= (async () => {
    const store = storage()
    if (!store) return
    try {
      for (const event of readQueue(store)) {
        await httpClient.post('/client-events', event)
        writeQueue(
          store,
          readQueue(store).filter(
            (e) => e.occurredAt !== event.occurredAt || e.type !== event.type,
          ),
        )
      }
    } catch {
      /* sin red o servidor caído: se reintenta más tarde */
    } finally {
      flushing = null
    }
  })()
  return flushing
}

/** Apunta una incidencia en la cola e intenta mandarla ya. */
export function recordClientEvent(event: Omit<ClientEvent, 'occurredAt'>): void {
  const store = storage()
  if (!store) return
  writeQueue(store, [...readQueue(store), { ...event, occurredAt: new Date().toISOString() }])
  void flushClientEvents()
}
